import { useEffect } from 'react';
import { useListContext } from 'react-admin';
import { useSearchStore } from '../../hooks/useSearchStore';
import { useLocation } from 'react-router-dom';

interface SearchIntegrationProps {
  filterKey?: string;
}

/**
 * Component that integrates header search with React Admin's filter system
 * Must be used inside a List component where ListContext is available
 */
export function SearchIntegration({ filterKey = 'q' }: SearchIntegrationProps) {
  const listContext = useListContext();
  const { searchTerm, currentResource } = useSearchStore();
  const location = useLocation();

  // Get current resource from URL
  const pageResource = (() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts[0];
  })();

  // Apply search filter when search term changes
  useEffect(() => {
    if (!listContext) return;

    // Only apply filter if search is for current resource
    const activeSearchTerm = (currentResource === pageResource) ? searchTerm : '';
    
    const currentFilters = listContext.filterValues || {};
    const newFilters = { ...currentFilters };

    if (activeSearchTerm.trim()) {
      newFilters[filterKey] = activeSearchTerm;
    } else {
      delete newFilters[filterKey];
    }

    // Only update if filters actually changed
    const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(newFilters);
    if (filtersChanged) {
      listContext.setFilters(newFilters, []);
    }
  }, [searchTerm, currentResource, pageResource, filterKey, listContext]);

  return null; // This component renders nothing, it just handles the effect
}